import {
  ArrowRight,
  BookOpen,
  Bot,
  Code2,
  CreditCard,
  Database,
  LifeBuoy,
  LockKeyhole,
  Search,
  ShieldCheck,
  X,
} from "lucide-react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Brand } from "../components/Brand";
import { renderSafeMarkdown } from "../../widget/src/markdown";
import apexGuide from "../../docs/APEX_INTEGRATION.md?raw";
import { useState } from "react";
import { appConfig } from "../lib/config";

const articles = [
  {
    category: "Primeiros passos",
    title: "Crie o primeiro projeto e teste uma resposta",
    description: "Do workspace vazio à primeira resposta com fontes.",
    icon: Bot,
    why: "Projeto é a fronteira que separa configuração, conhecimento e histórico de cada produto.",
    steps: [
      "Crie ou selecione um workspace.",
      "Cadastre um projeto para cada produto independente.",
      "Publique ao menos uma fonte e use o teste do assistente antes da instalação.",
    ],
  },
  {
    category: "Oracle APEX",
    title: "Instale o widget na Página 0",
    description: "Condições para páginas públicas, modais, drawers e iframes.",
    icon: Code2,
    why: "A região global deve existir só em páginas autenticadas e normais, evitando duplicidade e vazamento de contexto.",
    steps: [
      "Crie uma região Static Content na Página 0.",
      "Aplique a condição server-side indicada no snippet.",
      "Crie o Ajax Callback de bootstrap e valide origem, assinatura e expiração.",
    ],
  },
  {
    category: "Segurança",
    title: "Proteja contexto e identidade com JWT",
    description: "Asserção HS256 curta, origem exata, replay e sessão opaca.",
    icon: ShieldCheck,
    why: "IDs enviados diretamente pelo JavaScript podem ser alterados no DevTools e nunca autorizam acesso.",
    steps: [
      "Gere uma chave diferente por ambiente e guarde-a somente no servidor.",
      "Emita uma asserção de 60 a 120 segundos com usuário, tenant, projeto e jti.",
      "Troque-a por sessão opaca; não exponha APP_SESSION, segredo ou token em storage.",
    ],
  },
  {
    category: "Conhecimento",
    title: "Prepare arquivos para respostas melhores",
    description: "Versões, status, fontes e falhas de processamento.",
    icon: Database,
    why: "Respostas confiáveis dependem de conteúdo pequeno, atual, versionado e recuperável por projeto.",
    steps: [
      "Envie TXT ou Markdown em UTF-8, até 64 KiB por arquivo. Não envie PDF, DOCX, dados pessoais ou segredos. Comece com título, tarefa, passos e exceções.",
      "Upload recebido não é publicação. Confira o resultado por arquivo; uma atualização com falha preserva a última versão publicada. Retente apenas os arquivos que falharam.",
      "Fontes URL e GitHub usam HTTPS e conteúdo público; não há autenticação de repositório privado. Depois de atualizar a fonte, sincronize, confira a versão publicada e teste a pergunta novamente.",
    ],
  },
  {
    category: "Privacidade",
    title: "Entenda histórico, retenção e permissões",
    description: "Quem pode ler conversas e como tratar dados pessoais.",
    icon: LockKeyhole,
    why: "Métricas agregadas e conteúdo de conversa têm riscos e permissões diferentes.",
    steps: [
      "Conceda acesso ao conteúdo somente a papéis autorizados.",
      "Defina retenção e exportação antes de uso em produção.",
      "Não envie ao LLM dados que não sejam necessários à resposta.",
    ],
  },
  {
    category: "Cobrança",
    title: "Como funciona o período gratuito",
    description: "Trial de 7 dias, limite de 1 projeto e próximos passos.",
    icon: CreditCard,
    why: "O período gratuito permite conhecer o produto antes de ativar o plano.",
    steps: [
      "O primeiro workspace elegível recebe o teste vigente. A concessão é verificada no servidor; criar outra conta/workspace não renova o direito ao trial.",
      "Durante o teste, o workspace permite 1 projeto e 1 instalação. Uma instalação pode atender várias páginas; não há cobrança automática.",
      "Ao terminar o período, o assistente e as áreas operacionais são pausados. Minha conta, exportação pessoal e Ajuda permanecem acessíveis; cobrança depende do papel. Contratação online indisponível, sem extensão automática.",
    ],
  },
];

export function HelpPage() {
  const [query, setQuery] = useState("");
  const [params, setParams] = useSearchParams();
  const publicPage = !useLocation().pathname.startsWith("/app");
  const ids = [
    "primeiros-passos",
    "apex",
    "seguranca",
    "conhecimento",
    "privacidade",
    "trial",
  ];
  const selectedTitle =
    articles[ids.indexOf(params.get("article") ?? "")]?.title ?? null;
  function setSelectedTitle(title: string | null) {
    const next = new URLSearchParams(params);
    if (title)
      next.set(
        "article",
        ids[articles.findIndex((article) => article.title === title)],
      );
    else next.delete("article");
    setParams(next);
  }

  const filtered = articles.filter((article) =>
    `${article.category} ${article.title} ${article.description}`
      .toLowerCase()
      .includes(query.toLowerCase()),
  );
  const selected = articles.find((article) => article.title === selectedTitle);
  const Content = publicPage ? "main" : "div";

  return (
    <Content className={publicPage ? "legal-page" : "help-page"}>
      {publicPage ? (
        <header>
          <Brand />
          <Link to="/">Voltar ao site</Link>
        </header>
      ) : null}
      <section className="help-hero">
        <BookOpen />
        <h1>Como podemos ajudar?</h1>
        <p>
          Guias curtos para configurar, proteger e melhorar o seu assistente.
        </p>
        <label>
          <Search size={19} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label="Buscar na ajuda"
            placeholder="Busque por APEX, JWT, conhecimento, cobrança..."
          />
        </label>
      </section>
      <section className="help-articles">
        {filtered.map(({ category, title, description, icon: Icon }) => (
          <button
            type="button"
            key={title}
            onClick={() => setSelectedTitle(title)}
            aria-expanded={selectedTitle === title}
          >
            <Icon />
            <span>
              <small>{category}</small>
              <strong>{title}</strong>
              <p>{description}</p>
            </span>
            <ArrowRight size={18} />
          </button>
        ))}
      </section>
      {filtered.length === 0 ? (
        <p role="status">
          Nenhum guia encontrado. Tente “APEX”, “conhecimento” ou “trial”.{" "}
          <button type="button" onClick={() => setQuery("")}>
            Ver todos os guias
          </button>
        </p>
      ) : null}
      {selected ? (
        <section className="help-detail" aria-live="polite">
          <header>
            <div>
              <small>{selected.category}</small>
              <h2>{selected.title}</h2>
            </div>
            <button
              className="icon-button"
              type="button"
              aria-label="Fechar guia"
              onClick={() => setSelectedTitle(null)}
            >
              <X size={18} />
            </button>
          </header>
          <p>
            <strong>Por quê:</strong> {selected.why}
          </p>
          <ol>
            {selected.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
          {params.get("article") === "apex" ? (
            <div
              dangerouslySetInnerHTML={{
                __html: renderSafeMarkdown(apexGuide),
              }}
            />
          ) : null}
        </section>
      ) : null}
      <section className="support-panel">
        <LifeBuoy />
        <div>
          <h2>Ainda precisa de ajuda?</h2>
          <p>
            Os guias acima cobrem os passos principais. Se o seu caso não
            aparecer, escreva para o suporte informando o nome do workspace e o
            que você tentou fazer. Não envie senhas, chaves, documentos privados
            ou tokens. Não há prazo de resposta contratado nesta fase.
          </p>
        </div>
        <a
          className="button button--secondary"
          href={`mailto:${appConfig.supportEmail}`}
        >
          Falar com o suporte
        </a>
      </section>
    </Content>
  );
}
